// Shared config reading/normalisation for the two no-iframe entries:
//   index.jsx  (public/newFrame.js — page bootstrap, .famewall-embed data-* attrs)
//   widget.jsx (public/widget/famewall-widget.js — ESM mount() + <famewall-embed>)
// One place decides what a valid embed config looks like, so the snippet path
// and the integration path can never drift.

export const SUPPORTED_FORMATS = ['grid', 'carousel', 'slider', 'avatars'];

// The embed pages and the Framer plugin name the masonry layout "wall"; the
// snippet's data-format has always called it "grid". Accept both spellings.
const FORMAT_ALIASES = { wall: 'grid' };

export function normalizeFormat(value) {
  const raw = String(value || 'grid').toLowerCase();
  const format = FORMAT_ALIASES[raw] || raw;

  return SUPPORTED_FORMATS.indexOf(format) > -1 ? format : 'grid';
}

export function toPositiveInt(value) {
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) || parsed <= 0 ? null : parsed;
}

export function readConfig(element) {
  const dataSrc =
    element.getAttribute('data-src') ||
    element.getAttribute('data-wall') ||
    element.getAttribute('data-wall-url') ||
    '';

  const format = normalizeFormat(element.getAttribute('data-format'));
  const rawTheme = element.getAttribute('data-theme');
  const autoplayAttr = element.getAttribute('data-autoplay') || element.getAttribute('autoplay');

  // avatars are keyed by a collection id, not a wall url. data-collection is
  // the explicit attribute; data-src is accepted too so the snippet stays
  // uniform with every other format.
  const collectionId =
    element.getAttribute('data-collection') ||
    element.getAttribute('data-collection-id') ||
    (format === 'avatars' ? dataSrc : '');

  return {
    format: format,
    wallUrl: dataSrc,
    collectionId: collectionId,
    limit: toPositiveInt(element.getAttribute('data-limit')),
    theme: rawTheme === 'dark' || rawTheme === 'light' ? rawTheme : '',
    debug: element.getAttribute('data-debug') === '1',
    autoplay: autoplayAttr === '1' ? '1' : '0',
    spacing: element.getAttribute('data-spacing') || '',
  };
}

// Options object (mount(el, options) / <famewall-embed> attrs) -> the same
// config shape readConfig produces, so SeoEmbedApp can't tell the entries
// apart.
export function normalizeOptions(options) {
  const opts = options || {};

  const format = normalizeFormat(opts.layout || opts.format);
  const wallUrl = String(opts.wall || opts.wallUrl || opts.src || '').trim();
  const collectionId = String(
    opts.collection || opts.collectionId || (format === 'avatars' ? wallUrl : '') || '',
  ).trim();

  return {
    format: format,
    wallUrl: wallUrl,
    collectionId: collectionId,
    limit: toPositiveInt(opts.limit),
    theme: opts.theme === 'dark' || opts.theme === 'light' ? opts.theme : '',
    debug: opts.debug === true || opts.debug === '1' || opts.debug === 1,
    autoplay: opts.autoplay === true || opts.autoplay === '1' || opts.autoplay === 1 ? '1' : '0',
    spacing: opts.spacing === undefined || opts.spacing === null ? '' : String(opts.spacing),
  };
}

// Every format needs an identifier before it can mount: avatars key off a
// collection id, every other format off a wall url.
export function hasRequiredIdentifier(config) {
  if (config.format === 'avatars') {
    return Boolean(config.collectionId);
  }

  return Boolean(config.wallUrl);
}
